import { useState } from 'react';
import {
  useDeleteIntegration,
  useGetAvailableIntegrations,
  useGetIntegrations
} from 'apis/business/setting/integrations/hook';
import Grid from '@mui/material/Grid2';
import { Box, MenuItem, Select, Typography, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import Button from 'components/Common/Button';
import Search from 'components/Common/Search';
import { Outlet, useNavigate } from 'react-router-dom';
import { CiWifiOff } from 'react-icons/ci';
import { formatDate } from 'utils/helperFunctions';
import { IoWifiSharp } from 'react-icons/io5';
import { useGetLocations } from '@/apis/business/setting/location/hook';
import IntegrationsLoader from './Components/Loader';
import LocationIcon from '@/assets/icons/LocationIcon';
import { GoPlus } from 'react-icons/go';
import { useGetCurrentUser } from '@/apis/user/hook';
import { CacheKeys } from '@/utils/cacheKeys';

export default function Integrations() {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { data: currentUser } = useGetCurrentUser();
  const locationIds = JSON.parse(localStorage.getItem(CacheKeys.locationIds) ?? '[]');
  const currentLocation = locationIds?.length > 0 ? locationIds[0] : null;
  const [searchText, setSearchText] = useState<null | string>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | ''>(
    currentUser?.role === 'location_admin' ? String(currentLocation) : ''
  );
  const navigate = useNavigate();

  const { data: locations } = useGetLocations();
  const { data: availableIntegrations, isLoading: isAvailableIntegrationLoading } = useGetAvailableIntegrations({
    per_page: 9999
  });
  const { data: integrations, isLoading: isIntegrationLoading } = useGetIntegrations({
    'filter[name_or_catalog_name_cont]': searchText,
    'filter[locations_id_eq]': selectedLocation || null,
    include: 'locations',
    per_page: 9999
  });
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();

  const isLocationAdmin = currentUser?.role === 'location_admin';

  const combinedData =
    integrations?.data?.map((item) => {
      const matchingIntegration = availableIntegrations?.data?.find(
        (integration: any) => integration.code === item.code
      );
      return {
        ...item,
        description: matchingIntegration?.description,
        sub_name: matchingIntegration?.name,
        avatar_url: matchingIntegration?.small_avatar_url,
        connection_type: matchingIntegration?.connection_type
      };
    }) ?? [];

  return (
    <>
      <Box
        display='flex'
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        mb={{ xs: 2, lg: 2 }}
        gap={{ xs: 2, lg: 0 }}
      >
        {/* Left side */}
        <Box>
          <Typography variant='h6' fontWeight={500} mb={0.5}>
            Integrations
          </Typography>
          <Typography color='text.secondary' fontSize={16} sx={{ display: { xs: 'none', sm: 'block' } }}>
            Manage your integrations and connect external services
          </Typography>
        </Box>

        {/* Right side */}
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={1}
          alignItems='center'
          width={{ xs: '100%', lg: 'auto' }}
        >
          <Search
            size='small'
            placeholder='Search Integration'
            searchText={searchText}
            fullWidth={isLargeScreen ? false : true}
            setSearchText={setSearchText}
            sx={{
              minWidth: { xs: '100%', sm: 220 },
              width: { xs: '100%', lg: 'auto' },
              bgcolor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                minHeight: 36
              }
            }}
          />
          {!isLocationAdmin && (
            <Select
              labelId='locations-label'
              name='locations_id'
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              displayEmpty
              size='small'
              sx={{
                borderRadius: '4px',
                bgcolor: 'white',
                minHeight: 36,
                minWidth: { xs: '100%', sm: 150 },
                width: { xs: '100%', lg: 'auto' },
                '.MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ececec'
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  height: 36
                }
              }}
            >
              <MenuItem value=''>All Locations</MenuItem>
              {locations?.data?.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          )}

          <Button
            onClick={() => navigate('add')}
            variant='contained'
            color='primary'
            startIcon={<GoPlus />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap'
            }}
          >
            Add Connection
          </Button>
        </Box>
      </Box>

      {isIntegrationLoading || isAvailableIntegrationLoading ? (
        <IntegrationsLoader />
      ) : (
        <Grid container spacing={{ xs: 2, lg: 2 }} mt={{ xs: 2, lg: 3 }}>
          {combinedData?.map((item, index) => {
            return (
              <Grid
                key={index}
                size={{ xs: 12, md: 6, lg: 4 }}
                bgcolor='white'
                p={{ xs: 1.5, lg: 2 }}
                border='1px solid #ECECEC'
                borderRadius={2}
                display='flex'
                flexDirection='column'
              >
                <Box
                  display='flex'
                  justifyContent='space-between'
                  alignItems='flex-start'
                  flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                  gap={{ xs: 1, lg: 0 }}
                >
                  <Box display='flex' alignItems='center' gap={1.5} marginBottom={{ xs: 1, lg: 2 }} flex={1}>
                    <img src={item.avatar_url} alt={item.name} style={{ width: 32, height: 32 }} />
                    <Box display='flex' flexDirection='column' minWidth={0}>
                      <Typography fontSize={16} fontWeight={500} lineHeight={1} marginBottom={0.2} noWrap>
                        {item.name}
                      </Typography>
                      <Typography fontSize={14} color='text.secondary' lineHeight={1} noWrap>
                        {item.sub_name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display='flex' flexDirection='row' gap={1}>
                    {!isLocationAdmin && (
                      <Tooltip
                        title={<LocationTooltipComponent item={item} />}
                        placement='top'
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'black',
                              color: 'white',
                              fontSize: '0.75rem',
                              borderRadius: 1,
                              p: 1
                            }
                          }
                        }}
                      >
                        <Box display='flex' flexDirection='row' sx={{ cursor: 'pointer' }} color='#7a7878ff'>
                          <Box mt={0.3}>
                            <LocationIcon size={18} />
                          </Box>
                          <Typography fontSize={14}>{item?.locations?.length}</Typography>
                        </Box>
                      </Tooltip>
                    )}
                    <Box fontSize={20} mt={0.2} marginBottom={3} color={item?.active ? '#228B22' : '#FF6347'}>
                      {item?.active ? <IoWifiSharp /> : <CiWifiOff />}
                    </Box>
                  </Box>
                </Box>
                <Typography my={1} fontSize={14} lineHeight={1.5}>
                  {`${item?.description ? item?.description.slice(0, 80) : 'No Description'}...`}
                </Typography>
                <Typography mb={1} color='secondary' fontSize={11}>
                  Modified at : {formatDate(item.updated_at)}
                </Typography>
                <Box flexGrow={1} />
                <Box
                  display='flex'
                  justifyContent='flex-end'
                  gap={1.5}
                  flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
                  mt={{ xs: 1, lg: 0 }}
                >
                  <Button
                    size='small'
                    onClick={() => navigate(`${item.id}`)}
                    color={item?.active || item?.inactivate_at ? 'info' : 'primary'}
                    sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 'auto' } }}
                  >
                    {item?.active ? 'Configure' : item?.inactivate_at ? 'Reactivate' : 'Activate'}
                  </Button>
                  <Button
                    size='small'
                    onClick={() => deleteIntegration(item.id!)}
                    color='error'
                    sx={{ minWidth: { xs: 'calc(50% - 6px)', sm: 'auto' } }}
                  >
                    {item?.active ? 'Deactivate' : 'Delete'}
                  </Button>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Outlet />
    </>
  );
}

const LocationTooltipComponent = ({ item }: { item: any }) => {
  const locations = item?.locations || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography fontSize={12} fontWeight={600} mb={0.5}>
        Locations using this Integration
      </Typography>
      {locations.length > 0 ? (
        locations.map((loc: any) => (
          <Typography key={loc.id} fontSize={12} lineHeight={1.4}>
            • {loc.name}
          </Typography>
        ))
      ) : (
        <Typography fontSize={12} fontStyle='italic'>
          No Locations
        </Typography>
      )}
    </Box>
  );
};
